/**
 * Buyer Type Module - B2B/B2C Mode Switching
 * Handles state management, localStorage, cart attributes, and price display
 */
(function() {
  'use strict';

  const STORAGE_KEYS = {
    buyerType: 'buyer_type',
    orgNr: 'org_nr'
  };

  const CART_ATTRIBUTES = {
    buyerType: 'Buyer type',
    orgNr: 'Org nr'
  };

  const BUYER_TYPES = {
    private: 'private',
    company: 'company'
  };

  const VAT_RATES = {
    SE: 0.25,
    DK: 0.25,
    FI: 0.255,
    NO: 0.25
  };

  const DEFAULT_TRANSLATIONS = {
    switchToCompany: 'Switch to company',
    switchToPrivate: 'Switch to private customer',
    private: 'Private customer',
    company: 'Company',
    vatIncluded: 'Incl. VAT',
    vatExcluded: 'Excl. VAT',
    orgNrPersonalWarning: 'This looks like a personal identity number. Preferably enter an organization number or VAT number for sole proprietorships.',
    saveError: 'An error occurred. Please try again.'
  };

  function getTranslation(key) {
    return window.FoxTheme?.buyerTypeStrings?.[key] || DEFAULT_TRANSLATIONS[key] || '';
  }

  // State
  let state = {
    buyerType: BUYER_TYPES.private,
    orgNr: ''
  };

  /**
   * Initialize the module
   */
  function init() {
    loadFromStorage();
    updateUI();
    updateVatLabels();
    syncCartAttributes();
    bindEvents();
    updateAllPrices();
  }

  /**
   * Update all VAT labels on the page
   */
  function getScopedElements(root, selector) {
    const scope = root || document;
    const elements = Array.from(scope.querySelectorAll(selector));

    if (scope.matches?.(selector)) {
      elements.unshift(scope);
    }

    return elements;
  }

  function updateVatLabels(root = document) {
    const labelText = state.buyerType === BUYER_TYPES.company 
      ? getTranslation('vatExcluded')
      : getTranslation('vatIncluded');
    
    getScopedElements(root, '[data-vat-label]').forEach(el => {
      el.textContent = labelText;
    });
  }

  /**
   * Load state from localStorage
   */
  function loadFromStorage() {
    const storedType = localStorage.getItem(STORAGE_KEYS.buyerType);
    const storedOrgNr = localStorage.getItem(STORAGE_KEYS.orgNr);

    if (storedType && Object.values(BUYER_TYPES).includes(storedType)) {
      state.buyerType = storedType;
    }
    if (storedOrgNr) {
      state.orgNr = storedOrgNr;
    }
  }

  /**
   * Save state to localStorage
   */
  function saveToStorage() {
    localStorage.setItem(STORAGE_KEYS.buyerType, state.buyerType);
    if (state.buyerType === BUYER_TYPES.company && state.orgNr) {
      localStorage.setItem(STORAGE_KEYS.orgNr, state.orgNr);
    } else {
      localStorage.removeItem(STORAGE_KEYS.orgNr);
    }
  }

  /**
   * Sync cart attributes with current state
   */
  async function syncCartAttributes(force = false) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const currentAttrs = cart.attributes || {};

      const expectedType = state.buyerType === BUYER_TYPES.company ? 'Company' : 'Private';
      const expectedOrgNr = state.buyerType === BUYER_TYPES.company ? state.orgNr : '';

      // Only update if values differ
      if (force || 
          currentAttrs[CART_ATTRIBUTES.buyerType] !== expectedType ||
          currentAttrs[CART_ATTRIBUTES.orgNr] !== expectedOrgNr) {
        await updateCartAttributes(expectedType, expectedOrgNr);
      }
    } catch (error) {
      console.error('Error syncing cart attributes:', error);
    }
  }

  /**
   * Update cart attributes via Shopify API
   */
  async function updateCartAttributes(buyerType, orgNr) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            [CART_ATTRIBUTES.buyerType]: buyerType,
            [CART_ATTRIBUTES.orgNr]: orgNr
          }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      throw error;
    }
  }

  /**
   * Set buyer type and save
   */
  async function setBuyerType(type, orgNr = '') {
    if (!Object.values(BUYER_TYPES).includes(type)) {
      console.error('Invalid buyer type:', type);
      return;
    }

    state.buyerType = type;
    state.orgNr = type === BUYER_TYPES.company ? orgNr : '';

    saveToStorage();
    updateUI();
    await syncCartAttributes(true);
    updateAllPrices();

    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('buyerTypeChanged', {
      detail: {
        buyerType: state.buyerType,
        orgNr: state.orgNr,
        isCompany: state.buyerType === BUYER_TYPES.company
      }
    }));
  }

  /**
   * Get current state
   */
  function getState() {
    return { ...state };
  }

  /**
   * Check if current mode is company
   */
  function isCompany() {
    return state.buyerType === BUYER_TYPES.company;
  }

  /**
   * Update all UI elements based on current state
   */
  function updateUI() {
    // Update toggle buttons
    document.querySelectorAll('[data-buyer-type-toggle]').forEach(el => {
      const isCompanyMode = state.buyerType === BUYER_TYPES.company;
      el.setAttribute('data-current-type', state.buyerType);
      
      const textEl = el.querySelector('[data-buyer-type-toggle-text]');
      if (textEl) {
        textEl.textContent = isCompanyMode 
          ? el.dataset.textPrivate || getTranslation('switchToPrivate')
          : el.dataset.textCompany || getTranslation('switchToCompany');
      }
    });

    // Update cart info displays
    document.querySelectorAll('[data-buyer-type-cart-info]').forEach(el => {
      updateCartInfoDisplay(el);
    });

    // Update VAT labels
    updateVatLabels();

    // Show/hide company-specific elements
    document.querySelectorAll('[data-show-if-company]').forEach(el => {
      el.style.display = state.buyerType === BUYER_TYPES.company ? '' : 'none';
    });

    document.querySelectorAll('[data-show-if-private]').forEach(el => {
      el.style.display = state.buyerType === BUYER_TYPES.private ? '' : 'none';
    });
  }

  /**
   * Update cart info display element
   */
  function updateCartInfoDisplay(el) {
    const typeEl = el.querySelector('[data-buyer-type-value]');
    const orgNrEl = el.querySelector('[data-org-nr-value]');
    const orgNrWrapper = el.querySelector('[data-org-nr-wrapper]');

    if (typeEl) {
      typeEl.textContent = state.buyerType === BUYER_TYPES.company 
        ? getTranslation('company')
        : getTranslation('private');
    }

    if (orgNrWrapper) {
      orgNrWrapper.style.display = state.buyerType === BUYER_TYPES.company && state.orgNr 
        ? '' 
        : 'none';
    }

    if (orgNrEl) {
      orgNrEl.textContent = state.orgNr || '';
    }
  }

  /**
   * Calculate price with VAT
   */
  function getVatRate() {
    const countryCode = window.FoxTheme?.market?.countryCode || window.Shopify?.country || 'SE';
    return VAT_RATES[countryCode] ?? VAT_RATES.SE;
  }

  function calculatePriceWithVat(priceCents) {
    return Math.round(priceCents * (1 + getVatRate()));
  }

  /**
   * Format price using the active Shopify money format.
   */
  function formatPrice(priceCents) {
    if (window.FoxTheme?.Currency?.formatMoney && window.FoxTheme?.settings?.moneyFormat) {
      return window.FoxTheme.Currency.formatMoney(priceCents, window.FoxTheme.settings.moneyFormat);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'SEK',
      minimumFractionDigits: priceCents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(priceCents / 100);
  }

  /**
   * Update all price elements on the page
   */
  function updateAllPrices(root = document) {
    getScopedElements(root, '[data-price-cents]').forEach(el => {
      updatePriceElement(el);
    });
  }

  /**
   * Re-apply buyer type prices and VAT labels to existing or injected markup.
   */
  function refresh(root = document) {
    updateAllPrices(root);
    updateVatLabels(root);
  }

  /**
   * Update a single price element
   */
  function updatePriceElement(el) {
    const originalCents = parseInt(el.dataset.priceCents, 10);
    if (isNaN(originalCents)) return;

    const displayCents = state.buyerType === BUYER_TYPES.private
      ? calculatePriceWithVat(originalCents)
      : originalCents;

    el.innerHTML = formatPrice(displayCents);
  }

  /**
   * Validate org number - check if it looks like a personal number
   */
  function validateOrgNr(orgNr) {
    const cleaned = orgNr.replace(/[\s-]/g, '');
    const warnings = [];

    // Check if it looks like a personal number (10 or 12 digits)
    if (/^\d{10}$/.test(cleaned) || /^\d{12}$/.test(cleaned)) {
      // Could be personal number - check format
      if (/^(19|20)?\d{6}[-]?\d{4}$/.test(cleaned) || /^\d{6}[-]?\d{4}$/.test(cleaned)) {
        warnings.push(getTranslation('orgNrPersonalWarning'));
      }
    }

    return {
      isValid: true, // We allow saving anyway
      warnings
    };
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Toggle button clicks
    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('[data-buyer-type-toggle]');
      if (toggle) {
        e.preventDefault();
        openModal();
      }

      // Edit button in cart
      const editBtn = e.target.closest('[data-buyer-type-edit]');
      if (editBtn) {
        e.preventDefault();
        openModal();
      }
    });

    // Listen for buyerTypeChanged to update prices dynamically
    window.addEventListener('buyerTypeChanged', () => {
      updateAllPrices();
    });

    // Update prices when cart is updated (for AJAX cart)
    document.addEventListener('cart:updated', () => {
      setTimeout(updateAllPrices, 100);
    });

    // Re-apply VAT prices after variant change replaces price HTML
    document.addEventListener('variant:changed', () => {
      setTimeout(() => {
        updateAllPrices();
        updateVatLabels();
      }, 50);
    });
  }

  /**
   * Open the buyer type modal
   */
  function openModal() {
    const modal = document.getElementById('BuyerTypeModal');
    if (!modal) return;

    // Set current values
    const privateRadio = modal.querySelector('[data-buyer-type-radio="private"]');
    const companyRadio = modal.querySelector('[data-buyer-type-radio="company"]');
    const orgNrInput = modal.querySelector('[data-org-nr-input]');
    const orgNrField = modal.querySelector('[data-org-nr-field]');
    const warningEl = modal.querySelector('[data-org-nr-warning]');

    if (privateRadio) privateRadio.checked = state.buyerType === BUYER_TYPES.private;
    if (companyRadio) companyRadio.checked = state.buyerType === BUYER_TYPES.company;
    if (orgNrInput) orgNrInput.value = state.orgNr;
    if (orgNrField) orgNrField.style.display = state.buyerType === BUYER_TYPES.company ? '' : 'none';
    if (warningEl) warningEl.style.display = 'none';

    // Show modal
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Focus management
    const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();

    // Setup modal events
    setupModalEvents(modal);
  }

  /**
   * Close the modal
   */
  function closeModal() {
    const modal = document.getElementById('BuyerTypeModal');
    if (!modal) return;

    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Return focus to toggle button
    const toggle = document.querySelector('[data-buyer-type-toggle]');
    if (toggle) toggle.focus();
  }

  /**
   * Setup modal event listeners
   */
  function setupModalEvents(modal) {
    const closeBtns = modal.querySelectorAll('[data-modal-close]');
    const overlay = modal.querySelector('[data-modal-overlay]');
    const saveBtn = modal.querySelector('[data-modal-save]');
    const radios = modal.querySelectorAll('[data-buyer-type-radio]');
    const orgNrInput = modal.querySelector('[data-org-nr-input]');
    const orgNrField = modal.querySelector('[data-org-nr-field]');
    const warningEl = modal.querySelector('[data-org-nr-warning]');

    // Close handlers
    const handleClose = (e) => {
      e.preventDefault();
      closeModal();
    };

    closeBtns.forEach(btn => {
      btn.addEventListener('click', handleClose, { once: true });
    });
    overlay?.addEventListener('click', handleClose, { once: true });

    // ESC key
    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', handleKeydown);
      }
      // Focus trap
      if (e.key === 'Tab') {
        trapFocus(e, modal);
      }
    };
    document.addEventListener('keydown', handleKeydown);

    // Radio change - show/hide org nr field
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        const isCompany = radio.dataset.buyerTypeRadio === 'company';
        if (orgNrField) orgNrField.style.display = isCompany ? '' : 'none';
        if (warningEl) warningEl.style.display = 'none';
      });
    });

    // Org nr input - validate on blur
    orgNrInput?.addEventListener('blur', () => {
      if (orgNrInput.value) {
        const validation = validateOrgNr(orgNrInput.value);
        if (validation.warnings.length > 0 && warningEl) {
          warningEl.textContent = validation.warnings[0];
          warningEl.style.display = '';
        } else if (warningEl) {
          warningEl.style.display = 'none';
        }
      }
    });

    // Save handler
    saveBtn?.addEventListener('click', async (e) => {
      e.preventDefault();
      
      const selectedRadio = modal.querySelector('[data-buyer-type-radio]:checked');
      const newType = selectedRadio?.dataset.buyerTypeRadio || BUYER_TYPES.private;
      const newOrgNr = newType === BUYER_TYPES.company && orgNrInput ? orgNrInput.value.trim() : '';

      saveBtn.disabled = true;
      saveBtn.classList.add('loading');

      try {
        await setBuyerType(newType, newOrgNr);
        closeModal();
      } catch (error) {
        console.error('Error saving buyer type:', error);
        alert(getTranslation('saveError'));
      } finally {
        saveBtn.disabled = false;
        saveBtn.classList.remove('loading');
      }
    }, { once: true });
  }

  /**
   * Trap focus within modal
   */
  function trapFocus(e, modal) {
    const focusables = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API globally
  window.BuyerType = {
    getState,
    setBuyerType,
    isCompany,
    openModal,
    closeModal,
    updateAllPrices,
    updateVatLabels,
    refresh,
    formatPrice,
    calculatePriceWithVat,
    BUYER_TYPES
  };
})();
